import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { SearchLimitService, SearchType } from 'projects/zbs-app/src/app/search-limit.service';
import { Configuration } from 'projects/zbs-app/src/app/services/Ebootis.Queries/Configuration';
import { CustomerSearch } from 'projects/zbs-app/src/app/services/Ebootis.Queries/CustomerSearch';
import { LogisticsAreaType } from 'projects/zbs-app/src/app/services/Ebootis.Shared/LogisticsAreaType';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogisticAreaService } from '../../../shared/logistic-area/logistic-area.service';
import { CustomerService } from '../../customer.service';

@Component({
	selector: 'app-customer-search',
	templateUrl: './customer-search.component.html',
	styleUrls: ['./customer-search.component.scss'],
})
export class CustomerSearchComponent implements OnInit, OnDestroy {
	@Input() set forCompany(p_forCompany: boolean) {
		this.m_forCompany = p_forCompany;
	}
	get forCompany(): boolean {
		return this.m_forCompany;
	}
	private m_forCompany: boolean = false;
	@Input() set selectionMode(value: string) {
		this.m_selectionMode = value;
	}

	get selectionMode(): string {
		return this.m_selectionMode;
	}

	@Input() set customerSaved(p_saved: boolean) {
		if (p_saved == true) {
			this.searchCustomer_Submit();
		}
	}

	@Output() public customerEdit: EventEmitter<string> = new EventEmitter();
	@Output() public customersSelected: EventEmitter<CustomerSearch.Customer | CustomerSearch.Customer[]> = new EventEmitter();

	@ViewChild('formDirective', { static: true }) public formDirective: FormGroupDirective;

	public readonly HIERARCHY_NODES_1: string = 'hierarchyNode1';
	public readonly HIERARCHY_NODES_2: string = 'hierarchyNode2';
	public readonly HIERARCHY_NODES_3: string = 'hierarchyNode3';
	public readonly HIERARCHY_NODES_4: string = 'hierarchyNode4';
	public readonly INCLUDE_FOREIGN_PUBLIC: string = 'includeForeignPublic';
	public readonly INCLUDE_OWN_PUBLIC: string = 'includeOwnPublic';
	public readonly INCLUDE_OWN_PRIVATE: string = 'includeOwnPrivate';
	public readonly LOGISTIC_AREA_TYPE: LogisticsAreaType = LogisticsAreaType.CUSTOMER;

	private m_nodes: string[] = [];
	private m_subscriptionsSubject = new Subject();
	private m_selectionMode: string;
	public m_isLoading: boolean = false;
	public m_displayError: HttpErrorResponse;
	public m_messageHandlerClass: string = 'info';
	public m_configuration: Configuration.Result;
	public m_node1Result: string[];
	public m_node2Result: string[];
	public m_node3Result: string[];
	public m_node4Result: string[];
	public m_customerList: CustomerSearch.Customer[];

	public m_customerSearchForm: UntypedFormGroup = this.m_formBuilder.group({
		id: [],
		name: [],
		street: [],
		zip: [],
		city: [],
		country: [],
		logisticArea: [this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE), Validators.required],
		[this.HIERARCHY_NODES_1]: [],
		[this.HIERARCHY_NODES_2]: [],
		[this.HIERARCHY_NODES_3]: [],
		[this.HIERARCHY_NODES_4]: [],
		[this.INCLUDE_OWN_PUBLIC]: [true],
		[this.INCLUDE_OWN_PRIVATE]: [true],
		[this.INCLUDE_FOREIGN_PUBLIC]: [true],
		limit: [this.searchLimit.getSetLimit(SearchType.CUSTOMER), Validators.required],
	});

	constructor(
		private m_customerService: CustomerService,
		private m_configurationService: ConfigurationService,
		private m_logisticAreaService: LogisticAreaService,
		private m_formBuilder: UntypedFormBuilder,
		public searchLimit: SearchLimitService
	) { }

	getLimitList() {
		return this.searchLimit.getLimitList(SearchType.CUSTOMER);
	}

	setLimit(p_value: number) {
		this.searchLimit.setLimit(SearchType.CUSTOMER, p_value);
	}

	trimValue(p_formControl: AbstractControl) {
		p_formControl.setValue(p_formControl.value.trim());
	}

	/**
	 * die Konfiuration sowie die Hierarchiestufe 1 wird geladen
	 */
	public ngOnInit(): void {
		// Ausgewählte Konfiguration laden
		this.m_configurationService.getConfiguration().pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (confiuration) => {
				this.m_configuration = confiuration;

			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			},
		});
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}


	/**
	 * Die Kundensuche wird ausgelöst. Die eingegebenen Suchparameter werden falls ausgefüllt gesetzt.
	 * Die Lister der erhaltenen Kunden wird esetzt um diese an die Tabelle zu senden.
	 */
	public searchCustomer_Submit(): void {
		this.m_isLoading = true;
		const params = new CustomerSearch.Params();

		params.config = this.m_configurationService.SelectedConfigOid;
		params.logisticsAreaProxy = this.getControl('logisticArea').value ? this.getControl('logisticArea').value : undefined;
		params.id = this.getControl('id').value ? this.getControl('id').value : undefined;
		params.name = this.getControl('name').value ? this.getControl('name').value : undefined;
		params.street = this.getControl('street').value ? this.getControl('street').value : undefined;
		params.zip = this.getControl('zip').value ? this.getControl('zip').value : undefined;
		params.city = this.getControl('city').value ? this.getControl('city').value : undefined;
		params.country = this.getControl('country').value ? this.getControl('country').value : undefined;
		params.hierarchyNode1 = this.getControl(this.HIERARCHY_NODES_1).value;
		params.hierarchyNode2 = this.getControl(this.HIERARCHY_NODES_2).value;
		params.hierarchyNode3 = this.getControl(this.HIERARCHY_NODES_3).value;
		params.hierarchyNode4 = this.getControl(this.HIERARCHY_NODES_4).value;
		params.includeOwnPrivate = this.m_configuration?.accessAllAddresses ? this.getControl(this.INCLUDE_OWN_PRIVATE).value : true;
		params.includeOwnPublic = this.m_configuration?.accessAllAddresses ? this.getControl(this.INCLUDE_OWN_PUBLIC).value : true;
		params.includeForeignPublic = this.m_configuration?.accessAllAddresses ? this.getControl(this.INCLUDE_FOREIGN_PUBLIC).value : false;
		params.limit = this.getControl('limit').value ? this.getControl('limit').value : undefined;

		this.m_customerService.searchCustomer(params).pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (data) => {
				if (data != null && data.length > 0) {
					this.m_customerList = data;
				} else {
					this.m_customerList = [];
				}
				this.m_isLoading = false;
			},
			error: (error) => {
				this.m_isLoading = false;
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			},
		});
	}
	public setIncludeOwnPublic(p_checkevent: MatCheckboxChange) {
		this.getControl(this.INCLUDE_OWN_PUBLIC).setValue(p_checkevent.checked);
	}
	/**
	 * Formular der Kundensuche wird zurückgesetzt und im anschluss Defaultwerte angegeben.
	 */
	public resetCustomerSearchForm_Click(): void {
		this.m_customerList = undefined;
		this.m_nodes = [];
		this.m_node2Result = null;
		this.m_node3Result = null;
		this.m_node4Result = null;
		this.formDirective.resetForm();
		this.m_customerSearchForm.reset();
		this.getControl('logisticArea').setValue(this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE));
		this.getControl('limit').setValue(this.searchLimit.getSetLimit(SearchType.CUSTOMER));
		this.getControl(this.INCLUDE_OWN_PRIVATE).setValue(true);
		this.getControl(this.INCLUDE_OWN_PUBLIC).setValue(true);
		this.getControl(this.INCLUDE_FOREIGN_PUBLIC).setValue(true);
	}

	/**
	 * Setzt ein FormControl zurück.
	 * @param p_controlName FormControlName
	 */
	public clearInput_Click(p_controlName): void {
		this.getControl(p_controlName).setValue('');
	}

	/**
	 * Setzt den eingegebenen Logistikbereich.
	 * @param p_area Logistikbereich OID
	 */
	public setLogisticArea(p_area: string): void {
		this.getControl('logisticArea').setValue(p_area);
	}

	/**
	 * Ein ausgewählter Customer wird an das übergeordenete Component übergeben.
	 * @param p_customers Ausgewählter Customer
	 */
	public selectedCustomers(p_customers: CustomerSearch.Customer | CustomerSearch.Customer[]): void {
		this.customersSelected.emit(p_customers);
	}

	/**
	 * Eine Customer OID wird an das übergeordnete Component übergeben um den Customer in der Detailansicht zu laden.
	 * @param p_customer Customer OID
	 */
	public editCustomer(p_customer: string): void {
		this.customerEdit.emit(p_customer);
	}

	/**
	 * Gibt ein FormControl basierend auf dessen Name zurück.
	 * @param p_name FormControlName
	 */
	public getControl(p_name: string): AbstractControl {
		return this.m_customerSearchForm.get(p_name);
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayError = null;
	}
}
